/**
 * The one object that owns the WebSocket (FE-ADR-017).
 *
 * Kept outside React so the connection's lifetime is explicit: `start()` once
 * the caller is authenticated in a school, `stop()` on logout or school switch.
 * A connection token is minted for ONE membership, so a socket surviving a
 * tenant switch would be a real isolation leak — teardown is a hard
 * `disconnect()` and a dropped client, never a re-auth of the live socket.
 *
 * Handlers are registered independently of the client. React children mount
 * their effects *before* the provider mounts its own, so a subscription
 * requested while the client is down must not be lost: the manager records the
 * wish and materializes it on the next `start()`. Re-establishing after a
 * school switch is safe by construction — every channel is re-authorized
 * server-side, and a channel from the previous tenant is answered with a 403.
 *
 * Reconnect/backoff is centrifuge-js's own (exponential with jitter); token
 * refresh is its `getToken` callback, so an expired JWT costs a round-trip
 * rather than a dropped connection.
 */
import { Centrifuge, UnauthorizedError, type Subscription } from 'centrifuge';

import type { ApiError } from '@/core/api';
import type { PublicationHandler, RealtimeStatus, Unsubscribe } from './types';

export interface RealtimeManagerOptions {
  url: string;
  getConnectionToken: () => Promise<string>;
  getSubscriptionToken: (channel: string) => Promise<string>;
  onStatus?: (status: RealtimeStatus) => void;
}

/**
 * A 401/403 from a token endpoint is a verdict, not a hiccup: the caller may
 * not have this channel, or is no longer signed in. Rethrowing as
 * `UnauthorizedError` tells centrifuge to stop retrying instead of hammering
 * the API on a loop that cannot succeed.
 */
function asTokenError(error: unknown): Error {
  const status = (error as ApiError)?.status;
  if (status === 401 || status === 403) return new UnauthorizedError('not permitted');
  return error instanceof Error ? error : new Error('token request failed');
}

export class RealtimeManager {
  private centrifuge: Centrifuge | null = null;
  /** What the app wants to watch — survives disconnects, not unmounts. */
  private handlers = new Map<string, Set<PublicationHandler>>();
  private subscriptions = new Map<string, Subscription>();
  private status: RealtimeStatus = 'disconnected';

  constructor(private readonly options: RealtimeManagerOptions) {}

  getStatus(): RealtimeStatus {
    return this.status;
  }

  isRunning(): boolean {
    return this.centrifuge !== null;
  }

  start(): void {
    if (this.centrifuge) return;

    const centrifuge = new Centrifuge(this.options.url, {
      getToken: async () => {
        try {
          return await this.options.getConnectionToken();
        } catch (error) {
          throw asTokenError(error);
        }
      },
    });

    centrifuge.on('connecting', () => this.setStatus('connecting'));
    centrifuge.on('connected', () => this.setStatus('connected'));
    centrifuge.on('disconnected', () => this.setStatus('disconnected'));

    this.centrifuge = centrifuge;
    centrifuge.connect();

    for (const channel of this.handlers.keys()) this.attach(channel);
  }

  /** Tear the socket down. Registered handlers are kept — components own those. */
  stop(): void {
    for (const channel of [...this.subscriptions.keys()]) this.detach(channel);

    const centrifuge = this.centrifuge;
    this.centrifuge = null;
    if (centrifuge) {
      centrifuge.removeAllListeners();
      centrifuge.disconnect();
    }
    this.setStatus('disconnected');
  }

  /**
   * Watch a channel. Several components may watch the same one (a thread page
   * and a thread list); they share one subscription and the last to leave takes
   * it down.
   */
  subscribe<T>(channel: string, handler: PublicationHandler<T>): Unsubscribe {
    const typed = handler as PublicationHandler;
    const existing = this.handlers.get(channel);
    if (existing) {
      existing.add(typed);
    } else {
      this.handlers.set(channel, new Set([typed]));
      this.attach(channel);
    }

    return () => {
      const set = this.handlers.get(channel);
      if (!set) return;
      set.delete(typed);
      if (set.size > 0) return;
      this.handlers.delete(channel);
      this.detach(channel);
    };
  }

  private attach(channel: string): void {
    const centrifuge = this.centrifuge;
    if (!centrifuge || this.subscriptions.has(channel)) return;

    const subscription =
      centrifuge.getSubscription(channel) ??
      centrifuge.newSubscription(channel, {
        getToken: async () => {
          try {
            return await this.options.getSubscriptionToken(channel);
          } catch (error) {
            throw asTokenError(error);
          }
        },
      });

    this.subscriptions.set(channel, subscription);
    subscription.on('publication', ctx => {
      for (const fn of this.handlers.get(channel) ?? []) fn(ctx.data);
    });
    subscription.subscribe();
  }

  private detach(channel: string): void {
    const subscription = this.subscriptions.get(channel);
    if (!subscription) return;
    this.subscriptions.delete(channel);
    subscription.removeAllListeners();
    subscription.unsubscribe();
    this.centrifuge?.removeSubscription(subscription);
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus?.(status);
  }
}
